/**
 * M11 saved views — the "Views" sidebar section below the Workspace group.
 * Lists the views the caller can see (star = shared tenant-wide by a manager);
 * clicking one lands on /search?view=<id>, where SearchView applies the stored
 * definition. An amber dot flags views whose tag filters name deleted tags.
 * "Manage views" opens rename / visibility / delete (owner or manager —
 * backend enforces the same rule). Refreshes on the `acuon:views-changed`
 * window event so a save on the search page shows up immediately.
 * Styled with the existing SidebarGroup.css classes to sit natively in the nav.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Bookmark, ChevronDown, ChevronRight, Star, Settings2, Pencil, Trash2, User, Users,
} from 'lucide-react';
import { cx, Button, Badge, Skeleton, EmptyState } from '../kit';
import { toast } from '../../utils/toast';
import { api, myRoleRank, ModalShell, ErrorCard } from '../cabinet/shared';

const EXPAND_KEY = 'sidebar-views-expanded';

function myIdentity() {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}');
    return (u.email || u.username || '').toLowerCase();
  } catch { return ''; }
}

// ── Manage modal: rename / visibility / delete ────────────────────────────────
function ManageViewsModal({ onClose }) {
  const manager = myRoleRank() >= 2;
  const me = myIdentity();
  const [views, setViews] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);    // {view_id, name, visibility, busy, error}
  const [deleting, setDeleting] = useState(null);  // view object
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try { setViews((await api('/api/v1/cabinet/views')).views || []); }
    catch (e) { setLoadError(e.message); setViews([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changed = () => {
    window.dispatchEvent(new CustomEvent('acuon:views-changed'));
    load();
  };

  const canEdit = (v) => manager || (v.owner || '').toLowerCase() === me;

  const saveEdit = async () => {
    const name = editing.name.trim();
    if (!name) return;
    setEditing((s) => ({ ...s, busy: true, error: null }));
    try {
      await api(`/api/v1/cabinet/views/${editing.view_id}`, 'PATCH',
        { name, visibility: editing.visibility });
      toast.success('View updated');
      setEditing(null);
      changed();
    } catch (e) { setEditing((s) => (s ? { ...s, busy: false, error: e.message } : s)); }
  };

  const doDelete = async () => {
    setDeleteBusy(true);
    try {
      await api(`/api/v1/cabinet/views/${deleting.view_id}`, 'DELETE');
      toast.success(`View “${deleting.name}” deleted`);
      setDeleting(null);
      changed();
    } catch (e) { toast.error(e.message); }
    finally { setDeleteBusy(false); }
  };

  const field = 'rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400';

  return (
    <ModalShell
      onClose={onClose} title="Manage saved views" icon={Bookmark} maxW="max-w-lg"
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      {views === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div>
      ) : loadError ? (
        <ErrorCard message={loadError} onRetry={load} />
      ) : views.length === 0 ? (
        <EmptyState icon={Bookmark} className="py-8" title="No saved views"
          description="Save one from the search page — set your filters, then “Save view”." />
      ) : (
        <div className="border border-gray-200 rounded-xl divide-y divide-gray-100">
          {views.map((v) => (
            <div key={v.view_id} className="px-3 py-2.5">
              {editing?.view_id === v.view_id ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text" value={editing.name} autoFocus
                      onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value, error: null }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveEdit(); } }}
                      aria-label={`Rename view ${v.name}`}
                      className={cx(field, 'flex-1')}
                    />
                    <Button size="sm" variant="primary" loading={editing.busy} disabled={!editing.name.trim()} onClick={saveEdit}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)} disabled={editing.busy}>Cancel</Button>
                  </div>
                  {manager && (
                    <span className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
                      {[['private', 'Only me', User], ['tenant', 'Everyone', Users]].map(([k, label, I]) => (
                        <button
                          key={k} type="button"
                          onClick={() => setEditing((s) => ({ ...s, visibility: k, error: null }))}
                          className={cx('inline-flex items-center gap-1.5 px-3 py-1.5 transition',
                            editing.visibility === k ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}
                        >
                          <I size={13} /> {label}
                        </button>
                      ))}
                    </span>
                  )}
                  {editing.error && <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{editing.error}</p>}
                </div>
              ) : deleting?.view_id === v.view_id ? (
                <div className="flex flex-wrap items-center gap-2 text-sm text-rose-700">
                  <span className="flex-1">
                    Delete the view <b>“{v.name}”</b>? Only the saved filters are removed — no documents are affected.
                  </span>
                  <Button size="sm" variant="danger" loading={deleteBusy} onClick={doDelete}>Delete view</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleting(null)} disabled={deleteBusy}>Cancel</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2.5">
                  <Bookmark size={14} className="text-indigo-400 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 truncate">{v.name}</span>
                    <span className="block text-xs text-gray-400 truncate">{v.owner}</span>
                  </span>
                  {(v.stale_tags || []).length > 0 && (
                    <span className="shrink-0" title={`Tag(s) ${v.stale_tags.join(', ')} no longer exist`}>
                      <Badge tone="amber">Stale tag</Badge>
                    </span>
                  )}
                  <Badge tone={v.visibility === 'tenant' ? 'indigo' : 'gray'}>
                    {v.visibility === 'tenant' ? 'Everyone' : 'Private'}
                  </Badge>
                  {canEdit(v) && (
                    <>
                      <Button size="sm" variant="ghost" title={`Rename ${v.name}`}
                        onClick={() => { setDeleting(null); setEditing({ view_id: v.view_id, name: v.name, visibility: v.visibility, busy: false, error: null }); }}>
                        <Pencil size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" title={`Delete ${v.name}`} className="text-rose-500 hover:text-rose-700"
                        onClick={() => { setEditing(null); setDeleting(v); }}>
                        <Trash2 size={13} />
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </ModalShell>
  );
}

// ── The sidebar section ───────────────────────────────────────────────────────
export default function SavedViewsSection({ isCollapsed, onNavigate }) {
  const location = useLocation();
  const [views, setViews] = useState([]);
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(EXPAND_KEY) !== 'false'; } catch { return true; }
  });
  const [showManage, setShowManage] = useState(false);

  const load = useCallback(() => {
    api('/api/v1/cabinet/views')
      .then((r) => setViews(r.views || []))
      .catch(() => setViews([]));   // section simply stays empty
  }, []);

  useEffect(() => {
    load();
    window.addEventListener('acuon:views-changed', load);
    return () => window.removeEventListener('acuon:views-changed', load);
  }, [load]);

  useEffect(() => {
    try { localStorage.setItem(EXPAND_KEY, String(expanded)); } catch { /* ignore */ }
  }, [expanded]);

  if (isCollapsed) return null;   // domain flyouts cover collapsed nav; views live one click away on /search

  const activeId = location.pathname === '/search'
    ? new URLSearchParams(location.search).get('view') : null;
  const plainButton = { width: '100%', background: 'transparent', border: 'none', cursor: 'pointer' };

  return (
    <div className="sidebar-domain">
      <button
        type="button"
        className={`domain-header ${expanded ? 'expanded' : 'collapsed'}`}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="domain-icon" aria-hidden="true"><Bookmark size={17} strokeWidth={1.9} /></span>
        <span className="domain-name">Views</span>
        <span className="domain-toggle">{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
      </button>

      {expanded && (
        <ul className="domain-submenu expanded-mode">
          {views.map((v) => (
            <li key={v.view_id} className="submenu-item">
              <NavLink
                to={`/search?view=${v.view_id}`}
                onClick={onNavigate}
                title={(v.stale_tags || []).length > 0
                  ? `Tag(s) ${v.stale_tags.join(', ')} no longer exist — that filter is dropped`
                  : (v.visibility === 'tenant' ? `Shared with everyone by ${v.owner}` : v.name)}
                className={() => `submenu-link ${activeId === v.view_id ? 'active' : ''}`}
              >
                <span className="submenu-icon" aria-hidden="true">
                  {v.visibility === 'tenant'
                    ? <Star size={15} strokeWidth={1.9} />
                    : <Bookmark size={15} strokeWidth={1.9} />}
                </span>
                <span className="submenu-label">{v.name}</span>
                {(v.stale_tags || []).length > 0 && (
                  <span
                    aria-label="Contains deleted tag filters"
                    className="inline-block w-2 h-2 rounded-full bg-amber-400 shrink-0"
                  />
                )}
              </NavLink>
            </li>
          ))}
          <li className="submenu-item">
            <button
              type="button"
              className="submenu-link"
              style={plainButton}
              onClick={() => setShowManage(true)}
            >
              <span className="submenu-icon" aria-hidden="true"><Settings2 size={15} strokeWidth={1.9} /></span>
              <span className="submenu-label">{views.length === 0 ? 'No saved views yet' : 'Manage views'}</span>
            </button>
          </li>
        </ul>
      )}

      {showManage && <ManageViewsModal onClose={() => setShowManage(false)} />}
    </div>
  );
}
